import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useMaps } from './maps'
import { useTasksStore } from './tasks'
import { usePlanner } from './planner'
import { DEFAULT_TRACK_WIDTH } from './drawing'
import { rainbowColors } from '../helpers/rainbowColors'
import {
    postStartEditingPathPlanning,
    postEditPathSegmentPlanning,
    postEditedPathPlanning,
    postGetUncoveredArea
} from '../scripts/fieldsPlanning'
import { getPlan } from '../scripts/task'
import { updatePlan } from '../scripts/task'
import { useEasyModeStore } from './easyMode'
import {
    getClosestPointPolyline,
    getDistancePointToPolygon
} from '@/helpers/geometryHelper'
import nearestPointOnLine from '@turf/nearest-point-on-line'
import { point, lineString } from '@turf/turf'

export const useEditPathStore = defineStore('editPath', {
    state: () => ({
        currentPlanId: null,
        editingSegment: false,
        updatedSegment: false,
        startPoint: null,
        endPoint: null,
        markers: [],
        polylines: [],
        loading: false,
        errorMessage: '',
        pathData: null,
        originalPathData: {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    properties: {},
                    geometry: null
                }
            ]
        },
        checkpoints: [],
        turnRadius: 13.5,
        machineWidth: 14.0,
        fieldId: null,
        markerReferences: {},
        rainbowColors: rainbowColors,
        rainbowPolylines: [],
        extractedPath: [],
        updatedPathId: null,
        markersOnPath: false,
        editedPathData: {
            path: null,
            edit_start: {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        properties: {},
                        geometry: {
                            coordinates: [0, 0],
                            type: "Point"
                        }
                    }
                ]
            },
            edit_end: {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        properties: {},
                        geometry: {
                            coordinates: [0, 0],
                            type: "Point"
                        }
                    }
                ]
            },
            checkpoints: null,
            checkpoints_type: null,
            field_id: null,
            turn_radius: 14.0,
            machine_width: 14.0,
            task_type: null,
        },
        uncoveredArea: null,
        showOriginalPath: true,
        mapClickListener: null,
        startEditingError: false,
        pathCoordinatesToSave: null,
        _wasListeningBeforeMeasure: null,
    }),
    actions: {
        async initPathEditing(planId) {
            if (planId == null || planId === '') {
                throw new Error('Plan ID is required')
            }

            try {
                this.loading = true
                this.currentPlanId = planId
                const tasksStore = useTasksStore()

                if (!tasksStore.field) {
                    throw new Error('Tasks store field not initialized')
                }

                this.clearAllElements()

                const res = await getPlan(planId)

                if (!res || !res.geometry) {
                    throw new Error('Failed to get plan data')
                }

                this.originalPathData.features[0].geometry = res.geometry
                this.workingWidth = res.working_width
                this.editedPathData.task_type = res.type

                const fieldId = tasksStore.field.id
                if (fieldId) {
                    this.fieldId = fieldId
                } else {
                    console.warn('Field ID not found in tasksStore')
                }
                this.uncoveredArea = null
                this.uncoveredArea = await this.showUncoveredArea()
                this.removeMapClickListener()
                this.addMapClickListener()

                return true
            } catch (error) {
                console.error('Error initializing path editing:', error)
                this.errorMessage = 'Error initializing path editing: ' + error.message

                return false
            } finally {
                this.loading = false
            }
        },
        // Add a click listener to the map for selecting start and end points
        addMapClickListener() {
            const ms = useMaps()
            const ems = useEasyModeStore()
            const ps = usePlanner()
            if (this.mapClickListener) {
                return
            }
            this.mapClickListener = ms.map.addListener('click', async (event) => {
                const clickedPoint = {
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng()
                }
                let relevantPath = []
                if (ems.easyMode) {
                    relevantPath = ms.draw.field.abLines
                } else {
                    relevantPath = this.rainbowPolylines.length > 0 ? this.rainbowPolylines : ps.rainbowPolylines
                }
                if (!this.startPoint) {
                    const startPointCoord = this.getClosestPointTurf({ position: clickedPoint }, relevantPath) || clickedPoint
                    const startMarker = this.setPointMarker(startPointCoord, 'S', '#00FF00', 'Start Point')
                    if (ems.easyMode) {
                        ems.addStartContextMenu(startMarker)
                    }
                } else if (!this.endPoint && this.startPoint) {
                    const endPointCoord = this.getClosestPointTurf({ position: clickedPoint }, relevantPath) || clickedPoint
                    this.setPointMarker(endPointCoord, 'E', '#FF0000', 'End Point')

                    if (!this.originalPathData) {
                        console.error('originalPathData is null before starting segment edit')
                        this.errorMessage = 'Error: Path data not available. Please try reloading the page.'
                        return
                    }

                    if (!ems.easyMode) {
                        await this.startEditSegment()
                    } else {
                        // clear previous unlimited points
                        this.checkpoints = []
                        this.removeMapClickListener()
                        ems.addMapClickListenerPoints()
                        this.setPathOrange()
                        ems.generateSegment(false)
                    }
                }
            })
        },
        // Get the closest point on the path using Turf.js
        // use this method for snapping points to the path
        getClosestPointTurf(rawMarker, path) {
            const ems = useEasyModeStore()
            if (ems.easyMode && this.markers.length > 1) {
                return null
            }

            if (!rawMarker || !rawMarker.position) {
                return null
            }

            const p = rawMarker.position

            if (!path || !path.length) {
                return null
            }
            // finds the closest line on the path for the marker
            return getClosestPointPolyline(p, path)
        },
        checkMarkerPathStatus(rawMarker, dragging = false) {
            const ems = useEasyModeStore()
            const ps = usePlanner()
            const ms = useMaps()

            if (!rawMarker || !rawMarker.title) {
                console.error('Marker or marker title is missing')
                return
            }

            // Extract text from title (e.g., "Checkpoint 1" -> "CP1", "Start Point" -> "S")
            let originalText = ''
            if (rawMarker.title.includes('Checkpoint')) {
                const checkpointNum = rawMarker.title.replace('Checkpoint ', '')
                if (ems.easyMode && ems.freeDrawingBooleans[parseInt(checkpointNum) - 1]) {
                    originalText = `FP${checkpointNum}`
                } else {
                    originalText = `CP${checkpointNum}`
                }
            } else if (rawMarker.title === 'Start Point') {
                originalText = 'S'
            } else if (rawMarker.title === 'End Point') {
                originalText = 'E'
            }
            let path = null
            if (ems.easyMode) {
                path = ms.draw.field.abLines.concat(ms.draw.field.headlanes)
            } else {
                path = this.rainbowPolylines.length > 0 ? this.rainbowPolylines : ps.rainbowPolylines
            }

            if (!path || !path.length) {
                console.error('No valid path available')
                return
            }

            const isOnRainbow = this.checkIsOnRainbow(rawMarker, path)
            const originalScale = rawMarker.checkpointNumber ? 1.2 : 1.4
            if (isOnRainbow) {
                const pinElement = this.createStyledPinElement(originalScale, originalText, '#00FF00')
                rawMarker.content = pinElement
            } else if (dragging) {
                const pinElement = this.createStyledPinElement(originalScale, originalText, '#FFA500')
                rawMarker.content = pinElement
            } else {
                let color = '#FFA500'
                const closestPoint = this.getClosestPointTurf(rawMarker, path)
                if (closestPoint && this.markersOnPath) {
                    rawMarker.position = {
                        lat: closestPoint.lat,
                        lng: closestPoint.lng
                    }
                    color = '#00FF00'
                }

                const pinElement = this.createStyledPinElement(originalScale, originalText, color)
                rawMarker.content = pinElement
            }
        },
        checkIsOnRainbow(rawMarker, path) {
            if (rawMarker.title === 'Start Point' || rawMarker.title === 'End Point') {
                return true
            }
            const ms = useMaps()
            const TOLERANCE = 0.00000007

            if (!rawMarker?.position || !path?.length) {
                return false
            }

            return path.some(polyline =>
                ms.google.maps.geometry.poly.isLocationOnEdge(
                    rawMarker.position,
                    polyline,
                    TOLERANCE
                )
            )
        },
        createStyledPinElement(scale, text, background) {
            const ms = useMaps()

            const pinElement = new ms.google.maps.marker.PinElement({
                background: background,
                borderColor: '#000000',
                glyphColor: '#FFFFFF',
                scale: scale,
                glyphText: String(text)
            })

            return pinElement
        },
        removeMapClickListener() {
            if (this.mapClickListener) {
                this.mapClickListener.remove()
                this.mapClickListener = null
            }
        },
        pauseMapClickListener() {
            if (this.mapClickListener) {
                this.mapClickListener.remove()
                this.mapClickListener = null
                this._wasListeningBeforeMeasure = true
            }
        },
        resumeMapClickListener() {
            if (this._wasListeningBeforeMeasure) {
                this._wasListeningBeforeMeasure = false
                this.addMapClickListener()
            }
        },
        // Set Start and End Point Markers
        setPointMarker(point, text, color, title) {
            if (text === 'S') {
                this.startPoint = point
            } else if (text === 'E') {
                this.endPoint = point
            }
            const marker = this.createDraggableMarker(point, text, 1.4, color, title)
            const ms = useMaps()
            const rawMarker = toRaw(marker)
            const ems = useEasyModeStore()
            // Set markerId for CP/FP in easyMode
            if ((text.startsWith('CP') || text.startsWith('FP')) && ems.easyMode) {
                rawMarker.markerId = 'start_' + text
            }
            // Initial status relative to the path
            this.checkMarkerPathStatus(rawMarker)

            ms.google.maps.event.addListener(rawMarker, 'drag', () => {
                const pos = rawMarker.position
                let current = text

                if (rawMarker.title && rawMarker.title.includes('Checkpoint')) {
                    const num = rawMarker.title.replace('Checkpoint ', '')
                    current = `CP${num}`
                }

                const point = { lat: pos.lat, lng: pos.lng }

                if (current === 'S') {
                    this.startPoint = point
                } else if (current === 'E') {
                    this.endPoint = point
                } else if (ems.easyMode && (current.startsWith('CP') || current.startsWith('FP'))) {
                    const idx = parseInt(current.substring(2)) - 1
                    if (this.checkpoints[idx]) {
                        this.checkpoints[idx].features[0].geometry.coordinates = [point.lng, point.lat]
                    }
                }

                ems.updatedChanges = true
                ems.fullPathGenerated = false

                if (ems.easyMode && (current === 'S' || current === 'E') && this.startPoint && this.endPoint) {
                    this.updateOrangePath(7)
                } else if (ems.easyMode && rawMarker.title && rawMarker.title.includes('Checkpoint')) {
                    const num = rawMarker.title.replace('Checkpoint ', '')
                    if (ems.redPinIndex === parseInt(num)) {
                        ems.stopRedPinAnimation()
                    }
                }
            })

            ms.google.maps.event.addListener(rawMarker, 'dragend', async () => {
                let current = text

                if (rawMarker.title && rawMarker.title.includes('Checkpoint')) {
                    const num = rawMarker.title.replace('Checkpoint ', '')
                    current = `CP${num}`
                }

                if (ems.easyMode) {
                    if (current.startsWith('CP') || current.startsWith('FP')) {
                        const idx = parseInt(current.substring(2)) - 1
                        if (this.checkpoints[idx]) {
                            this.checkpoints[idx].features[0].geometry.coordinates = [
                                rawMarker.position.lng,
                                rawMarker.position.lat,
                            ]
                        }
                    }

                    if ((current === 'S' || current === 'E') && this.startPoint && this.endPoint) {
                        ems.updateOrangeVertices()
                    }
                }

                // Snap + style the marker
                this.checkMarkerPathStatus(rawMarker)

                // Synchronize start / end with the final marker position
                if (rawMarker.title === 'Start Point') {
                    const pos = { lat: marker.position.lat, lng: marker.position.lng }
                    const snapTo = ms.draw.field.abLines
                    const snappedPosition = ems.getClosestPointTurfEasy(pos, snapTo)
                    this.startPoint = {
                        lat: snappedPosition.lat,
                        lng: snappedPosition.lng,
                    }
                    if (!ems.easyMode) this.updateOrangePath()
                } else if (rawMarker.title === 'End Point') {
                    const pos = { lat: marker.position.lat, lng: marker.position.lng }
                    const snapTo = ms.draw.field.abLines
                    const snappedPosition = ems.getClosestPointTurfEasy(pos, snapTo)
                    this.endPoint = {
                        lat: snappedPosition.lat,
                        lng: snappedPosition.lng,
                    }
                    if (!ems.easyMode) this.updateOrangePath()
                }

                // Professional mode: update backend after snapping
                if (this.editingSegment && !ems.easyMode) {
                    await this.updateSegment()
                }
            })
            this.markers.push(marker)
            if (ems.easyMode) {
                ems.addDragendListenerToMarker(marker)
            }
            return marker
        },
        setPathOrange(tolerance = 0.5) {
            const coordinates = this.extractSubRoute(this.startPoint, this.endPoint, tolerance)
            const ps = usePlanner()
            
            const orangePolyline = ps.createRainbowPolyline(coordinates, '#FFA500')

            orangePolyline.forEach(polyline => {
                polyline.setOptions({ zIndex: 1000 })
            })

            this.polylines.push(...orangePolyline)
            
            // Make the rainbow polylines inactive - check both local and planner store
            const rainbowPolylines = this.rainbowPolylines.length > 0 ? this.rainbowPolylines : ps.rainbowPolylines
            
            rainbowPolylines.forEach(polyline => {
                if (polyline) {
                    polyline.setOptions({ strokeOpacity: 0.3 })
                }
            })
        },
        
        updateOrangePath(tolerance = 0.5) {
            this.clearOrangePath()
            this.setPathOrange(tolerance)
        },
        
        clearOrangePath() {
            // Find and remove only the orange polylines
            this.polylines = this.polylines.filter(polyline => {
                if (polyline && polyline.strokeColor === '#FFA500') {
                    toRaw(polyline).setMap(null)
                    return false
                }
                return true
            })
            
            // Restore the opacity of the rainbow polylines - check both local and planner store
            const ps = usePlanner()
            const rainbowPolylines = this.rainbowPolylines.length > 0 ? this.rainbowPolylines : ps.rainbowPolylines
            
            rainbowPolylines.forEach(polyline => {
                if (polyline) {
                    polyline.setOptions({ strokeOpacity: 1.0 })
                }
            })
        },
        extractSubRoute(startPoint, endPoint, tolerance = 0.5) {
            const geom = this.originalPathData.features[0].geometry
            // Take the array of points [ [lng,lat], ... ]
            const points = geom.type === 'MultiLineString' ? geom.coordinates.flat() : geom.coordinates

            // Transform to [lng, lat]
            const start = [startPoint.lng, startPoint.lat]
            const end = [endPoint.lng, endPoint.lat]

            const turfLine = lineString(points)

            const start_point = nearestPointOnLine(turfLine, point(start), { units: 'meters' })
            const end_point = nearestPointOnLine(turfLine, point(end), { units: 'meters' })

            const startIdx = start_point.properties.dist > tolerance ? 0 : start_point.properties.index
            const endIdx = end_point.properties.dist > tolerance ? points.length - 1 : end_point.properties.index

            if (startIdx === -1 || endIdx === -1) return [] 
            const from = Math.min(startIdx, endIdx)
            const to = Math.max(startIdx, endIdx)

            // Convert from [lng, lat] to {lat, lng} for Google Maps
            return points.slice(from, to + 1).map((coord) => ({
                lat: coord[1],
                lng: coord[0],
            }))
        },

        findNearestIndex(points, target) {
            let best = -1
            let bestD = Infinity
            for (let i = 0; i < points.length; i++) {
                const dx = points[i][0] - target[0]
                const dy = points[i][1] - target[1]
                const d = dx * dx + dy * dy // for local proximity
                if (d < bestD) {
                    bestD = d
                    best = i
                }
            }
            return best
        },
        createDraggableMarker(position, text, scale, background, title) {
            const ms = useMaps()

            const pinElement = new ms.google.maps.marker.PinElement({
                background: background,
                borderColor: '#000000',
                glyphColor: '#FFFFFF',
                scale: scale,
                glyphText: String(text),
            })
            
            const marker = new ms.google.maps.marker.AdvancedMarkerElement({
                position: position,
                map: toRaw(ms.map),
                title: title,
                content: pinElement
            })

            // Make it draggable
            marker.gmpDraggable = true

            return marker
        },
        // Start editing a segment of the path
        async startEditSegment() {
            this.loading = true
            this.editingSegment = true

            const ems = useEasyModeStore()
            const ts = useTasksStore()

            try {
                if (!this.originalPathData) {
                    throw new Error('Original path data not available')
                }

                this.editedPathData.path = this.originalPathData
                this.editedPathData.edit_start.features[0].geometry.coordinates = [
                    this.startPoint.lng,
                    this.startPoint.lat
                ]
                this.editedPathData.edit_end.features[0].geometry.coordinates = [
                    this.endPoint.lng, 
                    this.endPoint.lat
                ]
                this.editedPathData.turn_radius = this.turnRadius
                this.editedPathData.field_id = ts.field.id

                if (ems.easyMode) {
                    this.editedPathData.checkpoints = this.checkpoints
                }

                // console.log(JSON.stringify(this.editedPathData, null, 2))

                const result = await postStartEditingPathPlanning(this.editedPathData)
                if ((result.data && result.data.reason) || result.problem) {
                    const errorMessage = result?.data?.info || result?.problem
                    throw new Error(errorMessage || 'Failed to start segment editing')
                } else if (result) {
                    this.pathData = result
                    await this.processPathResponse(result)
                    this.displayCheckpoints(result.checkpoints)
                } else {
                    console.error('No response data from server when starting segment edit')
                    throw new Error('No response from server when starting segment edit')
                }

            } catch (error) {
                console.error('Error starting segment edit:', error)
                if (error.message.startsWith('Checkpoints are too far apart from each other')) {
                    this.errorMessage = error.message
                } else {
                    this.errorMessage = 'Error starting segment edit: ' + error.message
                }
                this.startEditingError = true
                this.editingSegment = false
            } finally {
                this.loading = false
            }
        },
        // Process and display the path response data
        async processPathResponse(response) {
            if (!response) return

            const ms = useMaps()

            try {
                this.pathData = response
                this.clearPathPolylines()

                if (
                    response.path_segment &&
                    response.path_segment.features &&
                    response.path_segment.features.length > 0
                ) {

                    const segment = response.path_segment.features[0]
                    const geoJsonData = {
                        type: 'FeatureCollection',
                        features: [{
                            type: 'Feature',
                            geometry: segment.geometry,
                            properties: { type: 'editing_segment' }
                        }]
                    }

                    ms.map.data.addGeoJson(geoJsonData)
                    this.displaySegment(segment)
                }

                if (response.checkpoints &&
                    response.checkpoints.features &&
                    response.checkpoints.features.length > 0) {

                    this.clearMarkersExceptStartEnd()
                    this.clearDirectionLines()
                    this.displayCheckpoints(response.checkpoints)
                }
            } catch (error) {
                console.error('Error processing path response:', error)
                this.errorMessage = 'Error processing path data: ' + error.message
            }
        },
        // Display the path segment on the map with an orange line
        displaySegment(segment) {
            const ms = useMaps()
            const ps = usePlanner()

            try {
                const path = this.convertToLatLngCoordinates(segment.geometry)

                if (path.length >= 2) {
                    this.markers[0].position = path[0]
                    this.markers[1].position = path[path.length - 1]

                    this.checkMarkerPathStatus(toRaw(this.markers[0]))
                    this.checkMarkerPathStatus(toRaw(this.markers[1]))

                    this.startPoint = path[0]
                    this.endPoint = path[path.length - 1]
                }

                const polyline = new ms.google.maps.Polyline({
                    path: path,
                    geodesic: true,
                    strokeColor: '#FFA500',
                    strokeOpacity: 1.0,
                    strokeWeight: 5,
                    zIndex: 999,
                    map: toRaw(ms.map)
                })

                this.polylines.push(polyline)
                ps.addDirectionalArrows([polyline], '#FFFFFF', '#000000')

            } catch (error) {
                console.error('Error displaying segment:', error)
                this.errorMessage = 'Error displaying segment: ' + error.message
            }
        },
        // Display checkpoint markers in professional mode
        displayCheckpoints(checkpoints) {
            this.clearMarkersExceptStartEnd()

            if (!checkpoints || !checkpoints.features || !checkpoints.features.length) {
                console.warn('No checkpoints to display')
                return
            }

            try {
                checkpoints.features.forEach((feature, index) => {
                    if (feature.geometry &&
                        feature.geometry.type === 'LineString' &&
                        feature.geometry.coordinates &&
                        feature.geometry.coordinates.length >= 2) {
                        this.createCheckpointPair(feature, index)
                    }
                })
            } catch (error) {
                console.error('Error displaying checkpoints:', error)
                this.errorMessage = 'Error displaying checkpoints: ' + error.message
            }
        },
        // Creates a checkpoint with two markers and a line
        createCheckpointPair(feature, index) {
            const [startCoord, endCoord] = feature.geometry.coordinates
            const startPosition = { lat: startCoord[1], lng: startCoord[0] }
            const endPosition = { lat: endCoord[1], lng: endCoord[0] }
            const checkpointNumber = index + 1

            const startMarker = this.createCheckpointMarker(startPosition, checkpointNumber, 1)
            const endMarker = this.createCheckpointMarker(endPosition, checkpointNumber, 2)

            const line = this.createConnectionLine(startPosition, endPosition, checkpointNumber)

            this.setupMarkerReferences(startMarker, endMarker, line, checkpointNumber)
            this.setupEventListeners(startMarker, endMarker, line, index)

            this.markers.push(startMarker, endMarker)
        },
        createCheckpointMarker(position, checkpointNumber, pointNumber) {
            const marker = this.createDraggableMarker(
                position,
                `${checkpointNumber}.${pointNumber}`,
                1.2,
                '#FFA500',
                `Checkpoint ${checkpointNumber}.${pointNumber}`
            )

            marker.featureIndex = checkpointNumber - 1
            marker.pointIndex = pointNumber - 1
            marker.checkpointNumber = checkpointNumber

            return marker
        },
        createConnectionLine(startPos, endPos, checkpointNumber) {
            const ms = useMaps()

            const line = new ms.google.maps.Polyline({
                path: [startPos, endPos],
                geodesic: true,
                strokeColor: '#FF0000',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: false,
                zIndex: 1000,
                map: toRaw(ms.map)
            })

            line.id = 'line_' + checkpointNumber
            this.polylines.push(line)

            return line
        },
        // Connects markers with their lines
        setupMarkerReferences(startMarker, endMarker, line, checkpointNumber) {
            const lineId = line.id
            startMarker.lineId = endMarker.lineId = lineId

            startMarker.markerId = 'start_' + checkpointNumber
            endMarker.markerId = 'end_' + checkpointNumber
            startMarker.partnerMarkerId = endMarker.markerId
            endMarker.partnerMarkerId = startMarker.markerId

            this.markerReferences[startMarker.markerId] = startMarker
            this.markerReferences[endMarker.markerId] = endMarker
        },
        // Sets up event listeners for the markers
        setupEventListeners(startMarker, endMarker, line, index) {
            const rawStartMarker = toRaw(startMarker)
            const rawEndMarker = toRaw(endMarker)

            this.checkMarkerPathStatus(rawStartMarker)
            this.checkMarkerPathStatus(rawEndMarker)

            this.setupMarkerDragEvents(rawStartMarker, line, 0, index)
            this.setupMarkerDragEvents(rawEndMarker, line, 1, index)
        },
        // Handles marker dragging
        setupMarkerDragEvents(marker, line, pointIndex, featureIndex) {
            const ms = useMaps()
            const ts = useTasksStore()

            ms.google.maps.event.addListener(marker, 'drag', () => {
                const path = line.getPath()
                const markerPos = marker.position
                const latLng = new ms.google.maps.LatLng(markerPos.lat, markerPos.lng)
                path.setAt(pointIndex, latLng)
                this.checkMarkerPathStatus(marker, true)
            })

            const field = ms.fields.find(f => f.id === toRaw(ts.field.id))
            const fieldGeometry = toRaw(field?.outer_border) || null

            ms.google.maps.event.addListener(marker, 'dragend', async () => {
                this.checkMarkerPathStatus(marker, false)

                const distanceToFieldBorder = getDistancePointToPolygon(marker.position, fieldGeometry)

                if (distanceToFieldBorder === null) return

                if (distanceToFieldBorder < 200) {  // 200 meters threshold, adjust as needed
                    await this.updateCheckpointPosition(marker, featureIndex, pointIndex)
                } else {
                    this.errorMessage = `Checkpoint is too far from the field border. Please place it closer to the field.`
                }
            })
        },
        // Update the path when a checkpoint is moved
        async updateCheckpointPosition(marker, featureIndex, pointIndex) {
            if (!marker || !this.pathData) return

            try {
                // Get the marker position using toRaw to handle Vue reactivity
                const position = toRaw(marker).position

                // Extract lat/lng directly as numbers
                const latLng = {
                    lat: position.lat,
                    lng: position.lng
                }

                // If markersOnPath is true, we need to get the snapped position from checkMarkerPathStatus
                if (this.markersOnPath) {
                    const ps = usePlanner()
                    let path = this.rainbowPolylines.length > 0 ? this.rainbowPolylines : ps.rainbowPolylines
                    const closestPoint = this.getClosestPointTurf(marker, path)
                    if (closestPoint) {
                        latLng.lat = closestPoint.lat
                        latLng.lng = closestPoint.lng
                    }
                }

                // Update the checkpoint position in the data
                if (this.pathData.checkpoints &&
                    this.pathData.checkpoints.features &&
                    this.pathData.checkpoints.features[featureIndex] &&
                    this.pathData.checkpoints.features[featureIndex].geometry) {

                    const geometry = this.pathData.checkpoints.features[featureIndex].geometry
                    if (geometry.coordinates && geometry.coordinates[pointIndex]) {
                        // Update only the specific point in the LineString that was moved
                        geometry.coordinates[pointIndex] = [latLng.lng, latLng.lat]

                        // Update the connecting line between markers
                        this.updateConnectingLine(marker)

                        // Request updated path based on the new checkpoint position
                        await this.updateSegment()
                    }
                }
            } catch (error) {
                console.error('Error updating checkpoint position:', error)
                this.errorMessage = 'Error updating checkpoint position: ' + error.message
            }
        },
        // Update the connecting line between checkpoint markers
        updateConnectingLine(marker) {
            try {
                if (!marker || !marker.lineId) {
                    return
                }

                // First remove all existing lines with this ID from the map and array
                this.polylines = this.polylines.filter(line => {
                    if (line.id === marker.lineId) {
                        toRaw(line).setMap(null)
                        return false
                    }
                    return true
                })

                // Find the partner marker for this checkpoint
                const partnerMarker = toRaw(this.markerReferences[marker.partnerMarkerId])
                if (!partnerMarker) {
                    console.warn(`Could not find partner marker with ID ${marker.partnerMarkerId}`)
                    return
                }

                // Get the current marker's position
                const markerRaw = toRaw(marker)
                let markerPos = markerRaw.position

                // Get the partner marker's position
                let partnerPos = partnerMarker.position

                // If markersOnPath is true, get the snapped positions
                if (this.markersOnPath) {
                    const ps = usePlanner()
                    let path = this.rainbowPolylines.length > 0 ? this.rainbowPolylines : ps.rainbowPolylines

                    const closestPoint = this.getClosestPointTurf(markerPos, path)
                    if (closestPoint) {
                        markerPos = closestPoint
                    }

                    const partnerClosestPoint = this.getClosestPointTurf(partnerMarker, path)
                    if (partnerClosestPoint) {
                        partnerPos = partnerClosestPoint
                    }
                }

                // Create start and end positions for the line
                const startPos = {
                    lat: markerPos.lat,
                    lng: markerPos.lng
                }

                const endPos = {
                    lat: partnerPos.lat,
                    lng: partnerPos.lng
                }

                // Extract the checkpoint number from the line ID and create the new connection line
                const checkpointNumber = marker.lineId.replace('line_', '')
                this.createConnectionLine(startPos, endPos, checkpointNumber)

            } catch (error) {
                console.error('Error updating connecting line:', error)
            }
        },
        // Update the segment after a checkpoint has been moved
        async updateSegment() {
            this.loading = true
            this.updatedSegment = false
            this.errorMessage = ''

            const ems = useEasyModeStore()
            const tasksStore = useTasksStore()

            if (ems.easyMode) {
                await this.startEditSegment()
            }

            try {
                if (!this.pathData || !this.pathData.checkpoints) {
                    throw new Error('No path data available')
                }

                if (!this.pathData.checkpoints.features ||
                    this.pathData.checkpoints.features.length === 0 ||
                    !this.pathData.checkpoints.features[0].geometry ||
                    this.pathData.checkpoints.features[0].geometry.type !== 'LineString') {

                    console.error('Invalid checkpoints data structure:', this.pathData.checkpoints)
                    throw new Error('Invalid checkpoints data structure')
                }

                this.editedPathData.path = this.originalPathData
                this.editedPathData.edit_start.features[0].geometry.coordinates = [
                    this.startPoint.lng,
                    this.startPoint.lat
                ]
                this.editedPathData.edit_end.features[0].geometry.coordinates = [
                    this.endPoint.lng, 
                    this.endPoint.lat
                ]
                this.editedPathData.checkpoints = this.pathData.checkpoints
                this.editedPathData.turn_radius = this.turnRadius

                this.editedPathData.track_width = tasksStore.field.track_width || DEFAULT_TRACK_WIDTH

                if (ems.easyMode) {
                    this.editedPathData.checkpoints = this.checkpoints
                    this.editedPathData.checkpoints_type = ems.checkpoints_type
                    this.editedPathData.field_id = tasksStore.field.id
                }

                // Call the 2nd step of the backend API to update the segment
                const result = await postEditPathSegmentPlanning(this.editedPathData)

                if (!result) {
                    throw new Error('Failed to update segment - empty response')
                }

                if (result.segment) {
                    // Clear only the path polylines, not the direction lines
                    this.clearPathPolylines()

                    if (result.segment.features && result.segment.features.length > 0) {
                        const segment = result.segment.features[0]
                        this.displaySegment(segment)
                        this.pathCoordinatesToSave = null
                    } else {
                        console.warn('Segment has no features to display')
                    }
                } else {
                    this.errorMessage = result.data.info || 'Failed to update segment - no segment data returned'
                }
            } catch (error) {
                console.error('Error updating segment:', error)
                this.errorMessage = 'Error updating segment: ' + error.message
            } finally {
                this.loading = false
            }
        },
        // Save the edited path
        async saveEditedPath() {
            this.loading = true
            try {
                if (!this.fieldId || !this.startPoint || !this.endPoint || !this.rainbowPolylines || this.rainbowPolylines.length === 0) {
                    throw new Error('Missing required data for saving')
                }

                if (this.pathCoordinatesToSave === null) {
                    throw new Error('No edited path available to save')
                }

                if (!this.currentPlanId) {
                    throw new Error('No plan ID available')
                }

                // Prepare the data for backend
                const data = {
                    id: parseInt(this.currentPlanId),
                    geometry: {
                        type: 'MultiLineString',
                        coordinates: this.pathCoordinatesToSave
                    }
                }
                // Check if the geometry has at least one segment with >= 2 points
                const hasValidSegment = data.geometry.coordinates.some(segment => segment.length >= 2)
                if (!hasValidSegment) {
                    this.errorMessage = 'No valid coordinates found in the geometry'
                    this.loading = false
                    return false
                }
                // Double check for saving polylines
                const coords = data.geometry.coordinates

                if (coords.length >= 3) {
                    // pi curve has a segment with a straight line 2 coords
                    // turn multiple coordinates
                    // hook curve has also a segment with a straight line 2 coord
                    // hook curve has also a turn with multiple coordinates
                    const segmentLengths = coords.slice(0, -1).map(seg => seg.length)
                    const firstSegmentLength = coords[0].length
                    const lastSegmentLength = coords[coords.length - 1].length

                    const areInitialSegmentsUniform = segmentLengths.every(
                        len => len === segmentLengths[0]
                    )

                    const isLastSegmentNotLongerThanFirst =
                        lastSegmentLength <= firstSegmentLength

                    const isInvalidPathSegmentation =
                        areInitialSegmentsUniform && isLastSegmentNotLongerThanFirst

                    if (isInvalidPathSegmentation) {
                        this.errorMessage = 'Invalid path segmentation'
                        this.loading = false
                        return false
                    }
                }

                // Step 1: Call the 3rd step of the backend API to get the edited path
                const result = await updatePlan(data)

                if (!result) {
                    throw new Error('Failed to generate edited path - invalid response from server')
                }

                this.updatedPathId = result.id

                // Step 2: Clear the editing state
                this.clearEditingState()
                this.clearAllElements()

                this.loading = false
                return true

            } catch (error) {
                console.error('Error saving edited path:', error)
                this.errorMessage = 'Error saving edited path: ' + error.message
                this.loading = false
                return false
            }
        },
        // Display the edited path on the map
        async displayEditedPath(pathData) {

            try {
                // Create and add the path to the map
                if (pathData && pathData.features && pathData.features.length > 0) {
                    const pathCoords = this.convertToLatLngCoordinates(pathData.features[0].geometry)

                    if (pathCoords && pathCoords.length > 0) {
                        this.createPath(pathCoords)

                        this.markers.forEach(marker => {
                            this.checkMarkerPathStatus(toRaw(marker))
                        })
                    }
                }

                return true

            } catch (error) {
                console.error('Error displaying edited path:', error)
                this.errorMessage = 'Error displaying edited path: ' + error.message
                return false
            }
        },
        // Clear direction lines
        clearDirectionLines() {
            try {
                // Find directional lines (with IDs starting with 'line_')
                const directionLines = this.polylines.filter(polyline =>
                    polyline && polyline.id && polyline.id.startsWith('line_')
                )

                // Remove these lines from the map
                directionLines.forEach(line => {
                    if (line) {
                        toRaw(line).setMap(null)
                    }
                })

                // Keep only non-directional lines
                this.polylines = this.polylines.filter(polyline =>
                    polyline && (!polyline.id || !polyline.id.startsWith('line_'))
                )

            } catch (error) {
                console.error('Error clearing direction lines:', error)
            }
        },
        // Convert GeoJSON coordinates to an array of LatLng objects
        convertToLatLngCoordinates(geometry) {
            if (!geometry || !geometry.coordinates) {
                console.error('Invalid geometry', geometry)
                return []
            }

            const coordinates = []

            try {
                // Handle different geometry types
                if (geometry.type === 'LineString') {
                    geometry.coordinates.forEach(coord => {
                        coordinates.push({
                            lat: coord[1],
                            lng: coord[0]
                        })
                    })
                } else if (geometry.type === 'MultiLineString') {
                    // For MultiLineString, we'll take the first linestring
                    if (geometry.coordinates && geometry.coordinates.length > 0) {
                        geometry.coordinates.forEach(lineString => {
                            lineString.forEach(coord => {
                                coordinates.push({
                                    lat: coord[1],
                                    lng: coord[0]
                                })
                            })
                        })
                    }
                } else if (geometry.type === 'Point') {
                    // Handle a single point
                    coordinates.push({
                        lat: geometry.coordinates[1],
                        lng: geometry.coordinates[0]
                    })
                }

                return coordinates

            } catch (error) {
                console.error('Error converting coordinates:', error)
                return []
            }
        },
        // Clear all elements and resources
        clearAllElements() {
            this.clearMarkers()
            this.clearPolylines()
            this.clearRainbowPolylines()
            this.removeMapClickListener()
        },
        // Clear all markers
        clearMarkers() {
            this.markers.forEach(marker => {
                if (marker) {
                    toRaw(marker).setMap(null)
                }
            })

            this.markers = []
            this.markerReferences = {}
        },
        // Clear all markers except start and end
        clearMarkersExceptStartEnd() {
            const ems = useEasyModeStore()

            if (ems.easyMode) {
                return
            }

            // Keep only the first two markers (start and end)
            const startEndMarkers = this.markers.slice(0, 2)

            // Remove all markers except the first two (start and end)
            this.markers.forEach((marker, index) => {
                if (index >= 2) {
                    if (marker) {
                        toRaw(marker).setMap(null)
                    }
                }
            })

            // Update the markers array to contain only start and end
            this.markers = startEndMarkers
        },
        // Clear all polylines
        clearPolylines() {
            this.polylines.forEach(polyline => {
                if (polyline) {
                    toRaw(polyline).setMap(null)
                }
            })

            this.polylines = []
        },
        // Clear the editing state
        clearEditingState() {
            this.editingSegment = false
            this.startPoint = null
            this.endPoint = null
            this.checkpoints = []
            this.updatedSegment = false
            this.showOriginalPath = true
            this.uncoveredArea = null
            this.startEditingError = false
            if (this._wasListeningBeforeMeasure !== null) {
                this._wasListeningBeforeMeasure = null
            }
        },
        // Clear all elements and reset state
        onLeaveEditPath() {
            this.clearAllElements()
            this.clearEditingState()
            this.clearPathPolylines()

            this.errorMessage = ''
            this.loading = false
            this.markersOnPath = true

            this.startPoint = null
            this.endPoint = null

            this.pathData = null
            this.originalPathData = {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        properties: {},
                        geometry: null
                    }
                ]
            }
            this.currentPlanId = null

            this.markerReferences = {}

            this.extractedPath = []
            const ems = useEasyModeStore()
            this.pathCoordinatesToSave = null
            ems.resetEasyModeState()
        },
        // Clear only path polylines, not direction lines
        clearPathPolylines() {
            // Clear Google Maps data features that are path-related
            const ms = useMaps()
            ms.map.data.forEach(feature => {
                if (feature.getProperty('type') === 'editing_segment') {
                    ms.map.data.remove(feature)
                }
            })

            // Find and remove path polylines (green/orange), but keep direction lines (red)
            const pathPolylines = this.polylines.filter(polyline =>
                polyline && (!polyline.id || !polyline.id.startsWith('line_'))
            )

            // Remove path polylines from map
            pathPolylines.forEach(polyline => {
                if (polyline) {
                    toRaw(polyline).setMap(null)
                }
            })

            // Remove path polylines from array
            this.polylines = this.polylines.filter(polyline =>
                polyline && polyline.id && polyline.id.startsWith('line_')
            )

        },
        createPath(coordinates) {
            const ps = usePlanner()

            // First clear any existing rainbow polylines
            this.clearRainbowPolylines()

            this.rainbowPolylines = ps.createRainbowPolyline(coordinates)
            ps.addDirectionalArrows(this.rainbowPolylines, '#FFFFFF', '#000000')

            // set zIndex of the rainbow polylines to 1000 for better visibility
            this.rainbowPolylines.forEach(polyline => {
                polyline.setOptions({ zIndex: 2000 })
            })

            return this.rainbowPolylines
        },
        clearRainbowPolylines() {
            this.rainbowPolylines.forEach((polyline) => {
                toRaw(polyline).setMap(null)
            })
            this.rainbowPolylines = []
        },
        // Display the edited path on the map
        async generateEditedPath() {
            this.updatedSegment = true
            const ems = useEasyModeStore()
            const ps = usePlanner()

            try {
                if (!this.pathData || !this.fieldId || !this.startPoint || !this.endPoint) {
                    throw new Error('Missing required data for path generation')
                }
                // Prepare data for backend
                this.editedPathData.path = this.originalPathData
                this.editedPathData.edit_start.features[0].geometry.coordinates = [
                    this.startPoint.lng,
                    this.startPoint.lat
                ]
                this.editedPathData.edit_end.features[0].geometry.coordinates = [
                    this.endPoint.lng,
                    this.endPoint.lat
                ]
                this.editedPathData.selected_path = this.extractSubRoute(this.startPoint, this.endPoint)
                this.editedPathData.checkpoints = this.pathData.checkpoints
                this.editedPathData.field_id = this.fieldId
                this.editedPathData.turn_radius = this.turnRadius
                this.editedPathData.machine_width = this.machineWidth

                if (ems.easyMode) {
                    this.editedPathData.checkpoints = this.checkpoints
                    this.editedPathData.checkpoints_type = ems.checkpoints_type
                    const tasksStore = useTasksStore()
                    this.editedPathData.field_id = tasksStore.field.id
                }

                // Generate the edited path
                const result = await postEditedPathPlanning(this.editedPathData)

                if (!result || !result.path) {
                    const hasInfo = result?.data?.info
                    throw new Error('Failed to generate path - ' + (hasInfo ? result?.data?.info : 'Unknown error'))
                }
                this.pathCoordinatesToSave = result.path.features[0].geometry.coordinates

                ps.showUncoveredPolygons(result, false)
                if (ps.uncoveredZonesPulsing) {
                    ps.startUncoveredPulsing()
                }
                this.uncoveredArea = result.uncovered_area
                // Display the path on map
                await this.displayEditedPath(result.path)

                return {
                    success: true,
                    warnings: result.warnings || []
                }

            } catch (error) {
                console.error('Error showing edited path:', error)
                return {
                    success: false,
                    error: error.message || 'Failed to show the edited path'
                }
            }
        },
        // Update markers and segment when markersOnPath (checkbox) is changed
        async updateMarkersAndSegment() {
            if (this.markersOnPath) {
                try {
                    // Update all checkpoint markers positions
                    for (const marker of this.markers) {
                        this.checkMarkerPathStatus(toRaw(marker))
                        await this.updateCheckpointPosition(toRaw(marker), marker.featureIndex, marker.pointIndex)
                    }
                } catch (error) {
                    console.error('Error updating markers and segment:', error)
                    this.errorMessage = 'Error updating markers and segment: ' + error.message
                }
            }
        },
        centerToMarker(marker) {
            const ms = useMaps()
            
            ms.map.setCenter({
                lat: marker.position.lat,
                lng: marker.position.lng
            })
        },
        async showUncoveredArea(id = null, geometry = null, workingWidth = null) {
            if (!geometry && !this.originalPathData?.features?.[0]?.geometry) return

            const data = {
                "path": {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: geometry ?? this.originalPathData.features[0].geometry
                        }
                    ]
                },
                "field_id": id ?? this.fieldId,
                "working_width": workingWidth ?? this.workingWidth
            }
            const result = await postGetUncoveredArea(data)
            const resultData = result.data || result || null
            if (!resultData || resultData.uncovered_area === null || resultData.uncovered_area === undefined) {
                console.error('Failed to get uncovered area - invalid server response')
                return
            }

            const ps = usePlanner()
            ps.showUncoveredPolygons(resultData, false)
            return resultData.uncovered_area
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEditPathStore, import.meta.hot))
}
